import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import {
  buttonRed,
  buttonRedText,
  greenVivid,
  primary,
  primaryAlt,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import ModuleLimits from "./ModuleLimits";
import dayjs from "dayjs";
import { addModuleToCart, clearCart } from "../../../../../actions/cartActions";
import { useDispatch } from "react-redux";

const Module = ({
  tenant,
  name,
  plan,
  title,
  description,
  setCheckoutPanelOpen,
  setSubscriptionPanelOpen,
  updateStatus,
  icon = "icon",
  stripePriceId,
  stripeSubscriptionId,
  stripeSubscriptionStatus,
  nextBillingDate,
  nextUsageResetDate,
  subscriptionStartDate,
  subscriptionEndDate,
}) => {
  // GENERAL
  const { t } = useTranslation();

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // **Find Subscription Object**
  const subscription = tenant.subscriptions.find((sub) => sub.name === name);

  // STATE
  const [isActive, setIsActive] = useState(subscription?.status === "active");
  const [pendingStatus, setPendingStatus] = useState(null);

  // REDUX
  const dispatch = useDispatch();

  // HANDLERS
  const handleActivateToggle = () => {
    if (subscription) {
      setPendingStatus(!isActive); // Store the **desired** status before changing UI
      updateStatus(tenant.tenantID, name, !isActive);
    }
  };

  const addModuleToCartHandler = (
    priceId,
    name,
    priceAmount,
    quantity,
    icon
  ) => {
    dispatch(addModuleToCart(priceId, name, priceAmount, quantity, icon));
  };

  // USE EFFECT
  // USE EFFECT - Update `isActive` only when the backend confirms
  useEffect(() => {
    if (pendingStatus !== null) {
      setIsActive(subscription?.status === "active"); // Only update after confirmation
      setPendingStatus(null); // Reset pending state
    }
  }, [subscription?.status]);

  // LOGS
  // console.log("tenant", tenant);
  console.log("name", name);
  // console.log("title", title);
  // console.log("description", description);

  // console.log("isActive", isActive);
  // console.log("plan", plan);
  // console.log("stripePriceId", stripePriceId);
  // console.log("stripeSubscriptionId", stripeSubscriptionId);
  // console.log("stripeSubscriptionStatus", stripeSubscriptionStatus);
  // console.log("nextBillingDate", nextBillingDate);

  return (
    <Flex w="100%" justify="space-between" gap="3rem" p="3rem 0rem 0rem">
      {/* LEFT START */}
      <Flex direction="column" gap={2} w="40%">
        <Heading fontSize="1rem" fontWeight="600" pr="2rem">
          {title}
        </Heading>
        <Text color={textSecondary} fontSize="sm" fontWeight="500" pr="2rem">
          {description}
        </Text>

        {name !== "treatmentPlans" && (
          <Text
            fontSize="0.8rem"
            fontWeight="500"
            color={primary}
            pr="2rem"
            m="1rem 0 0"
          >
            {t("module.comingSoon")}
          </Text>
        )}
      </Flex>
      {/* LEFT END */}

      {/* RIGHT START */}

      <Flex
        w="800px"
        direction="column"
        justify="flex-start"
        align="flex-start"
      >
        <Flex
          direction="column"
          w="100%"
          bg={backgroundLightMode}
          borderRadius="1rem"
          p="1.25rem 1.5rem 1.25rem"
          gap="1rem"
        >
          <Flex direction="column">
            {/* MODULE START */}
            <Box w="100%" bg={white} p="1.25rem 1.5rem" borderRadius="1rem">
              {/* MODULE TOP START */}
              <Flex
                w="100%"
                align="center"
                justify="space-between"
                p="0 0 0rem"
                // borderBottom={`1px solid ${newBorderMode}`}
              >
                <Flex align="center" justify="space-between" gap="2rem">
                  <Stack gap={0}>
                    {/* ACTIVATE TOGGLE */}
                    <Text
                      fontSize="0.8rem"
                      fontWeight="500"
                      color={textSecondary}
                    >
                      {t("module.status")}
                    </Text>
                    <Box h="1.5rem">
                      <Switch
                        className="green"
                        colorScheme="primary"
                        size="sm"
                        isChecked={isActive}
                        onChange={handleActivateToggle}
                        // isDisabled={name !== "treatmentPlans"}
                      />
                    </Box>
                  </Stack>

                  <Stack gap={0}>
                    {/* PLAN */}
                    <Text
                      fontSize="0.8rem"
                      fontWeight="500"
                      color={textSecondary}
                    >
                      {t("module.plan")}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={isActive ? textPrimaryMode : textSecondary}
                      fontWeight="600"
                      m="0px 0 0"
                    >
                      {t(`module.${plan}`)}
                    </Text>
                  </Stack>
                  {/* PRICE */}

                  <Stack gap={0}>
                    <Text
                      fontSize="0.8rem"
                      fontWeight="500"
                      color={textSecondary}
                    >
                      {t("module.payment")}
                    </Text>
                    {stripeSubscriptionStatus === "cancelling" && (
                      <>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color={isActive ? textPrimaryMode : textSecondary}
                        >
                          -
                        </Text>
                      </>
                    )}
                    {stripeSubscriptionStatus === "cancelled" && (
                      <>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color={isActive ? textPrimaryMode : textSecondary}
                        >
                          -
                        </Text>
                      </>
                    )}

                    {stripeSubscriptionStatus === "active" && (
                      <>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color={isActive ? textPrimaryMode : textSecondary}
                        >
                          {subscription?.priceAmount
                            ? `${(subscription.priceAmount / 100).toFixed(2)} €`
                            : "-"}
                        </Text>
                      </>
                    )}

                    {!stripeSubscriptionStatus && (
                      <>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color={isActive ? textPrimaryMode : textSecondary}
                        >
                          {subscription?.priceAmount
                            ? `${(subscription.priceAmount / 100).toFixed(2)} €`
                            : "-"}
                        </Text>
                      </>
                    )}

                    {/* {plan !== "free" ? (
                        <>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color={isActive ? textPrimaryMode : textSecondary}
                          >
                            {subscription?.priceAmount
                              ? `${(subscription.priceAmount / 100).toFixed(
                                  2
                                )} €`
                              : "0.00 €"}

                            <Text
                              as="span"
                              fontWeight="500"
                              color={textSecondary}
                              fontSize="xs"
                            >
                              {subscription?.isYearly
                                ? t("module.perYear")
                                : t("module.perMonth")}
                            </Text>
                          </Text>
                        </>
                      ) : (
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color={isActive ? textPrimaryMode : textSecondary}
                        >
                          -
                        </Text>
                      )} */}
                  </Stack>

                  {/* LIMIT */}
                  {/* <Stack gap={0}>
                    <Text
                      fontSize="0.8rem"
                      fontWeight="500"
                      color={textSecondary}
                    >
                      {t("module.limit")}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={isActive ? textPrimaryMode : textSecondary}
                      fontWeight="600"
                      m="0px 0 0"
                    >
                      10
                    </Text>
                  </Stack> */}
                  {/* PAYMENT DAY */}
                  {stripeSubscriptionStatus === "cancelling" && (
                    <Stack gap={0}>
                      <Text
                        fontSize="0.8rem"
                        fontWeight="500"
                        color={textSecondary}
                      >
                        {t("module.nextPayment")}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={isActive ? textPrimaryMode : textSecondary}
                        fontWeight="600"
                        m="0px 0 0"
                        textTransform="capitalize"
                      >
                        -
                      </Text>
                    </Stack>
                  )}

                  {stripeSubscriptionStatus === "cancelled" && (
                    <Stack gap={0}>
                      <Text
                        fontSize="0.8rem"
                        fontWeight="500"
                        color={textSecondary}
                      >
                        {t("module.limitRenew")}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={isActive ? textPrimaryMode : textSecondary}
                        fontWeight="600"
                        m="0px 0 0"
                        textTransform="capitalize"
                      >
                        {subscriptionEndDate
                          ? dayjs(subscriptionEndDate).format("D MMM YYYY")
                          : "-"}
                      </Text>
                    </Stack>
                  )}

                  {stripeSubscriptionStatus === "active" && (
                    <Stack gap={0}>
                      <Text
                        fontSize="0.8rem"
                        fontWeight="500"
                        color={textSecondary}
                      >
                        {t("module.nextPayment")}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={isActive ? textPrimaryMode : textSecondary}
                        fontWeight="600"
                        m="0px 0 0"
                        textTransform="capitalize"
                      >
                        {nextBillingDate
                          ? dayjs(nextBillingDate).format("D MMM YYYY")
                          : "-"}
                      </Text>
                    </Stack>
                  )}

                  {/* {!stripeSubscriptionStatus && (
                    <Stack gap={0}>
                      <Text
                        fontSize="0.8rem"
                        fontWeight="500"
                        color={textSecondary}
                      >
                        {t("module.limitRenew")}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={isActive ? textPrimaryMode : textSecondary}
                        fontWeight="600"
                        m="0px 0 0"
                        textTransform="capitalize"
                      >
                        {isActive
                          ? subscriptionStartDate
                            ? dayjs(subscriptionStartDate)
                                .add(1, "month")
                                .format("D MMM YYYY")
                            : "-"
                          : "-"}
                      </Text>
                    </Stack>
                  )} */}
                </Flex>

                <Flex align="center" justify="flex-end" direction="column">
                  {isActive && (
                    <Button
                      bg={plan !== "free" ? backgroundLightMode : primaryAlt}
                      color={plan !== "free" ? textSecondary : white}
                      size="md"
                      fontSize="0.8rem"
                      borderRadius="1rem"
                      fontWeight="500"
                      _hover={{
                        bg: plan !== "free" ? textPrimaryMode : textPrimaryMode,
                        color: plan !== "free" ? white : white,
                      }}
                      isDisabled={!isActive}
                      // onClick={() => setCheckoutPanelOpen(true)}
                      // onClick={() => {
                      //   addModuleToCartHandler(icon, stripePriceId, name, 128);
                      //   setCheckoutPanelOpen(true);
                      // }}
                      onClick={() => {
                        if (plan !== "free") {
                          setSubscriptionPanelOpen(true);
                        } else {
                          setCheckoutPanelOpen(true);
                        }
                      }}
                    >
                      {plan !== "free"
                        ? t("module.viewSubscription")
                        : t("module.upgrade")}
                    </Button>
                  )}
                  {/* {stripeSubscriptionStatus && (
                    <Text
                      fontSize="xs"
                      fontWeight="500"
                      color={textSecondary}
                      m="0.25rem 0 0"
                    >
                      {stripeSubscriptionStatus}
                    </Text>
                  )} */}
                </Flex>
              </Flex>

              {/* MODULE TOP END */}

              {/* MODULE BOTTOM START */}
              {stripeSubscriptionStatus === "cancelling" && (
                <Flex
                  bg={buttonRed}
                  w="100%"
                  borderRadius="1rem"
                  m="1rem 0 0"
                  p="0.75rem 2rem"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="500"
                    color={buttonRedText}
                    textAlign="center"
                    w="100%"
                  >
                    {t("module.subscriptionCancellingText", {
                      date: dayjs(nextBillingDate).format("D MMM YYYY"),
                    })}
                  </Text>
                </Flex>
              )}

              {stripeSubscriptionStatus === "cancelled" && (
                <Flex
                  bg={buttonRed}
                  w="100%"
                  borderRadius="10rem"
                  m="1rem 0 0"
                  p="0.5rem 1rem"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="500"
                    color={buttonRedText}
                    textAlign="center"
                    w="100%"
                  >
                    {t("module.subscriptionEndedText", {
                      date: dayjs(subscriptionEndDate).format("D MMMM YYYY"),
                    })}
                  </Text>
                </Flex>
              )}

              <Flex
                borderTop={`1px solid ${newBorderMode}`}
                w="100%"
                align="center"
                justify="space-between"
                p="1rem 0 0"
                m="1rem 0 0"
              >
                <ModuleLimits
                  isActive={isActive}
                  moduleName={name}
                  plan={plan}
                  nextBillingDate={nextBillingDate}
                  nextUsageResetDate={nextUsageResetDate}
                />
              </Flex>

              {/* MODULE BOTTOM END */}
            </Box>

            {/* MODULE END */}
          </Flex>
        </Flex>
      </Flex>
      {/* RIGHT END  */}
    </Flex>
  );
};

export default Module;
